import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AlertController, NavController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { IAppConfigurationProviderSpecific } from "../../core/interfaces/IAppConfigurationProvider";
import { INSPECTION_KINDS } from "../../core/interfaces/INSPECTION_KINDS";
import { InspectionState } from "../../core/interfaces/inspectionState";
import { AppStateService } from "../../core/services/appState.service";
import { isNullOrEmpty } from "../../gyzmo-commons/helpers/null.helper";
import { ThreadHelper } from "../../gyzmo-commons/helpers/thread.helper";
import { LoggerService } from "../../gyzmo-commons/services/logs/logger.service";
import { GENERAL_PHOTOS } from "../app.constants";

@Injectable({ providedIn: "root" })
export class InspectionRouter {
    urlHistory: string[] = [];

    constructor(private navController: NavController,
                private router: Router,
                private loggerService: LoggerService,
                private appStateService: AppStateService,
                private alertController: AlertController,
                private translateService: TranslateService,
                private appConfigurationProvider: IAppConfigurationProviderSpecific) {
    }

    public initializeHistory(inspectionState: InspectionState) {
        let currentUrl = this.router.url;

        this.urlHistory = [];
        this.urlHistory.push(currentUrl); // Ajout de la page de départ pour gestion du back

        switch (inspectionState.inspection.kind) {
            case INSPECTION_KINDS.DEPARTURE:
                this.urlHistory.push("/movement-departure");
                break;
            case INSPECTION_KINDS.BACK:
                this.urlHistory.push("/movement-return");
                break;
            case INSPECTION_KINDS.INTERMEDIATE:
                this.urlHistory.push("/mileage-entry");
                break;
        }

        if (inspectionState.inspection.checklists.length > 0) {
            this.urlHistory.push("/checklist-v2");
        }

        if (inspectionState.inspection.faces.length > 0) {
            for (let i = 0; i < inspectionState.inspection.faces.length; i++) {
                this.urlHistory.push("/face-v2/" + i);
            }
        }

        if (inspectionState.customisation.wear.visibility
            && (inspectionState.inspection.equipment.wear.length <= 0 || inspectionState.inspection.equipment.wearDefinitionModified)) {
            this.urlHistory.push("/equipment-tyres-definition");
        }

        if (inspectionState.customisation.wear.visibility) {
            this.urlHistory.push("/equipment-tyres-wear");
        }

        if (GENERAL_PHOTOS && inspectionState.customisation.generalPictures.length > 0) {
            this.urlHistory.push("/face-general-photo-v2");
        }

        this.urlHistory.push("/inspection-summary-v2");
    }

    public async navigateToNextPage(inspectionState: InspectionState,
                                    returnToSummary: boolean) {
        let currentUrl = this.router.url;

        if (returnToSummary) {
            await this.navigateToSummary(inspectionState);
        } else {
            let index = this.urlHistory.findIndex(value => value == currentUrl);
            if (index >= 0) {
                let nextUrl = this.urlHistory[index + 1];
                if (nextUrl == "/equipment-tyres-wear") {
                    if (isNullOrEmpty(inspectionState.inspection.equipment.modelTyreWear)
                        || isNullOrEmpty(inspectionState.inspection.equipment.modelBrakeWear)
                        || inspectionState.inspection.equipment.wear.length <= 0) {
                        nextUrl = this.urlHistory[index + 2];
                    }
                }
                await this.navController.navigateForward(nextUrl, {
                    state: {
                        state: inspectionState,
                    },
                });
                ThreadHelper.start(() => this.appStateService.saveInspectionState(nextUrl, inspectionState));
            } else {
                this.loggerService.error(this.constructor.name, "❌ Route not found in stack ! " + currentUrl);
                this.loggerService.error(this.constructor.name, "History stack :" + this.urlHistory);
            }
        }
    }

    public async navigateToPreviousPage(inspectionState: InspectionState,
                                        returnToSummary: boolean) {
        let currentUrl = this.router.url;

        if (returnToSummary) {
            await this.navigateToSummary(inspectionState);
        } else {
            let index = this.urlHistory.findIndex(value => value == currentUrl);
            if (index >= 0) {
                let nextUrl = this.urlHistory[index - 1];
                if (nextUrl == "/equipment-tyres-wear") {
                    if (isNullOrEmpty(inspectionState.inspection.equipment.modelTyreWear)
                        || isNullOrEmpty(inspectionState.inspection.equipment.modelBrakeWear)
                        || inspectionState.inspection.equipment.wear.length <= 0) {
                        nextUrl = this.urlHistory[index - 2];
                    }
                }
                await this.navController.navigateForward(nextUrl, {
                    state: {
                        state: inspectionState,
                        forceRefresh: true,
                    },
                });
            } else {
                this.loggerService.error(this.constructor.name, "❌ Route not found in stack ! " + currentUrl);
                this.loggerService.error(this.constructor.name, "History stack :" + this.urlHistory);
            }
        }
    }

    public async goToHome(stateToSave: InspectionState) {
        let alert = await this.alertController.create({
            header: this.translateService.instant("IONIC_Exit_the_inspection_process"),
            message: this.translateService.instant("IONIC_You_are_about_to_exit_the_inspection_process.")
                     + "\n"
                     + this.translateService.instant("IONIC_Do_you_want_to_save_the_data_you_have_entered?"),
            buttons: [
                {
                    text: this.translateService.instant("IONIC_Cancel"),
                    role: "cancel",
                },
                {
                    text: this.translateService.instant("IONIC_No"),
                    handler: async () => {
                        await this.appStateService.saveActiveInspection(null);
                        await this.appStateService.deleteInspectionState(stateToSave.inspection.id);
                        await this.appConfigurationProvider.navigateAfterConnectionWithForceRefresh();
                    },
                },
                {
                    text: this.translateService.instant("IONIC_Yes"),
                    handler: async () => {
                        await this.appStateService.saveActiveInspection(null);
                        let currentUrl = this.router.url;
                        await this.appStateService.saveInspectionState(currentUrl, stateToSave);
                        await this.appConfigurationProvider.navigateAfterConnectionWithForceRefresh();
                    },
                }],
        });
        void alert.present();
    }

    private async navigateToSummary(inspectionState: InspectionState) {
        void this.appStateService.saveInspectionState("/inspection-summary-v2", inspectionState);
        await this.navController.navigateForward("/inspection-summary-v2", {
            state: {
                state: inspectionState,
            },
        });
    }
}
